const getPhoneURL = (phone: string): string => {
  return `+1${phone.replace('+1', '').replace(/-/g, '').replace(/\(/g, '').replace(/\)/g, '').replace(/ /g, '')}`;
};

const formatFHIRDate = (dateString: string): string => {
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    throw new Error('Invalid date format. Please provide date in the format YYYY-MM-DD');
  }
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
};

const capitalize = (s: string): string => {
  return (s && s[0].toUpperCase() + s.slice(1)) || '';
};

/**
 * Parses API dateTime string into a Date object, a 12-hour display string
 * (e.g. "12:30 PM"), and the raw hour for time-of-day grouping.
 *
 * Supported formats:
 * - MM/DD/YYYY HH:mm
 * - YYYY-MM-DDTHH:mm:ss (ISO-like)
 */
const parseDateTime = (dateTime: string): { date: Date; time12h: string; hour: number } => {
  if (!dateTime || typeof dateTime !== 'string') {
    throw new Error('Invalid dateTime value. Expected a non-empty string.');
  }

  const trimmedDateTime = dateTime.trim();
  let date: Date;

  if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmedDateTime)) {
    const [datePart, timePartRaw] = trimmedDateTime.split(/\s+/);
    const [month, day, year] = datePart.split('/').map(Number);

    // Some payloads may include date only; default to midnight instead of crashing.
    const timePart = timePartRaw ?? '00:00';
    const [hour, minute] = timePart.split(':').map(Number);

    if (
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      Number.isNaN(year) ||
      Number.isNaN(hour) ||
      Number.isNaN(minute)
    ) {
      throw new Error(
        `Invalid dateTime format. Expected "MM/DD/YYYY HH:mm" or ISO and received "${dateTime}"`,
      );
    }

    date = new Date(year, month - 1, day, hour, minute);
  } else {
    // Handles ISO-like date time, e.g. 2026-06-10T09:00:00
    date = new Date(trimmedDateTime);
  }

  if (Number.isNaN(date.getTime())) {
    throw new Error(
      `Invalid dateTime format. Expected "MM/DD/YYYY HH:mm" or ISO and received "${dateTime}"`,
    );
  }

  const hour = date.getHours();
  const minute = date.getMinutes();
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const time12h = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
  return { date, time12h, hour };
};

export { capitalize, formatFHIRDate, getPhoneURL, parseDateTime };
